import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { clearAuthData, getCurrentUser } from "../../utils/auth";
import {
  AppBar,
  Toolbar,
  Box,
  Avatar,
  Button,
  GlobalStyles,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import StjgLogo from "./img/logo-stjg.png";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import GroupsIcon from "@mui/icons-material/Groups";

// Icons for dropdown
import ApartmentIcon from '@mui/icons-material/Apartment';
import LocationCityIcon from "@mui/icons-material/LocationCity";
import PeopleIcon from "@mui/icons-material/People";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import RoomIcon from "@mui/icons-material/Room";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import EmailIcon from "@mui/icons-material/Email";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const currentUser = getCurrentUser();
  const userRole = parseInt(currentUser?.ID_Role);

  // Define navigation items based on user role
  const getNavItems = () => {
    if (userRole === 1) {
      // Admin - All except pre-reservation
      return [
        { name: "RÉSERVATIONS", icon: <AccessTimeIcon />, path: "/reservation" },
        { name: "NAVETTES", icon: <DirectionsBusIcon />, path: "/navette" },
        { name: "UTILISATEURS", icon: <GroupsIcon />, path: "/utilisateurs" },
        { name: "REPORTING", icon: <GroupsIcon />, path: "/reporting" },
      ];
    } else if (userRole === 2) {
      // User - Only reservation and pre-reservation
      return [
        { name: "PRÉ-RÉSERVATIONS", icon: <CalendarMonthIcon />, path: "/pre-reservation" },
        { name: "RÉSERVATIONS", icon: <AccessTimeIcon />, path: "/reservation" },
      ];
    }
    return [];
  };

  const navItems = getNavItems();

  const pathToTab = navItems.reduce((acc, item) => {
    acc[item.path] = item.name;
    return acc;
  }, {});

  const activeTab = pathToTab[location.pathname] || "";

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Define dropdown menu items based on user role
  const getDropdownItems = () => {
    if (userRole === 1) {
      // Admin dropdown items
      return [
        { label: "Sociétés", icon: <ApartmentIcon />, path: '/societe' },
        { label: "Sites", icon: <LocationCityIcon />, path: '/site' },
        { label: "Utilisateurs", icon: <PeopleIcon />, path: '/utilisateurs' },
        { label: "Véhicules", icon: <LocalShippingIcon />, path: '/vehicule' },
        { label: "Trajets", icon: <AltRouteIcon />, path: '/trajets' },
        { label: "Tarif", icon: <AttachMoneyIcon/>, path:'/tarifs' },
        { label: "Pickups", icon: <RoomIcon />, path:'/Pickup'},
        { label: "Shifts", icon: <AccessTimeFilledIcon />, path:'/Shifts' },
        { label: "Emails", icon: <EmailIcon />, path:'/Emails' },
      ];
    } else if (userRole === 2) {
      // User dropdown items - empty or minimal
      return [];
    }
    return [];
  };

  const dropdownItems = getDropdownItems();

  // Define account link based on role
  const getAccountPath = () => {
    if (userRole === 1) {
      return '/AdminCompte';
    } else if (userRole === 2) {
      return '/MonCompte';
    }
    return '/MonCompte';
  };

  return (
    <>
      <GlobalStyles
        styles={{
          "*": { margin: 0, padding: 0, boxSizing: "border-box" },
          html: { height: "100%" },
          body: { height: "100%", backgroundColor: "#fff" },
          "#root": { height: "100%" },
        }}
      />
      <AppBar
        position="static"
        sx={{
          backgroundColor: "white",
          boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
          padding: "10px",
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {/* Left: Logo and Nav Items */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "30px" }}>
            <img src={StjgLogo} alt="STJG Logo" style={{ height: "60px" }} />
            <Box sx={{ display: "flex", gap: "15px" }}>
              {navItems.map((item) => (
                <Button
                  key={item.name}
                  component={Link}
                  to={item.path}
                  sx={{
                    color: activeTab === item.name ? "red" : "black",
                    textTransform: "none",
                    fontSize: "16px",
                    fontFamily: "Roboto, sans-serif, Lalezar",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    transition: "color 0.3s ease",
                    "&:hover": { color: "red", backgroundColor: "transparent" },
                    backgroundColor: "transparent",
                  }}
                >
                  {item.icon} {item.name}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Right: Profile Icon */}
          <Box>
            <Avatar
              sx={{ bgcolor: "red", fontWeight: "bold", cursor: "pointer" }}
              onClick={handleAvatarClick}
            >
              {currentUser?.Nom ? currentUser.Nom.charAt(0).toUpperCase() : 'U'}
              {currentUser?.Prenom ? currentUser.Prenom.charAt(0).toUpperCase() : 'S'}
            </Avatar>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  minWidth: "200px",
                  "& .MuiMenuItem-root": {
                    color: "black",
                    "& .MuiListItemIcon-root": {
                      color: "black",
                    },
                    "&:hover": {
                      backgroundColor: "red",
                      color: "white",
                      "& .MuiListItemIcon-root": {
                        color: "white",
                      },
                    },
                  },
                },
              }}
            >
              {/* Role-based dropdown items */}
              {dropdownItems.map((item) => (
                <MenuItem component={Link} to={item.path} key={item.label} onClick={handleClose}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText>{item.label}</ListItemText>
                </MenuItem>
              ))}
              
              {/* Show divider only if there are dropdown items */}
              {dropdownItems.length > 0 && <Divider />}
              
              {/* Account menu item */}
              <MenuItem onClick={handleClose} component={Link} to={getAccountPath()}>
                <ListItemIcon>
                  <AccountCircleIcon />
                </ListItemIcon>
                <ListItemText>Mon compte</ListItemText>
              </MenuItem>
              
              {/* Logout */}
              <MenuItem onClick={() => {
                handleClose();
                clearAuthData();
                navigate('/?logout=true');
              }}>
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText>Se déconnecter</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default Navbar;









// <MenuItem
//   key={item.label}
//   component={Link}
//   to={item.path} // add the path to your menu array
//   onClick={handleClose}
// >
//   <ListItemIcon>{item.icon}</ListItemIcon>
//   <ListItemText>{item.label}</ListItemText>
// </MenuItem>ea
// import { Link } from "rct-router-dom";

// // Inside your map for buttons
// <Button
//   key={item.name}
//   component={Link}
//   to={item.path} // add this path in your nav array
//   ...
// >
//   {item.icon} {item.name}
// </Button>
